// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { useGrammarPoints } from "../../i18n/localizedGrammar";
import Header from "../header/HeaderComponent";
import Footer from "../footer/FooterComponent";
import Seo from "../Seo/SeoComponent";
import { useJlptLevel } from "../../hooks/useJlptLevel";
import { useGrammarProgress } from "../../hooks/useGrammarProgress";
import { useAuth } from "../../context/AuthContext";
import { kanaToRomaji } from "../../utils/kanaToRomaji";
import { LearnedBadge, LearningStatusButton } from "../LearningStatus/LearningStatus";
import { useAchievementsSeen } from "../../hooks/useAchievementsSeen";
import { grammarCelebration } from "../../utils/achievementCelebration";
import { publishMilestone } from "../../utils/milestoneEvents";
import * as analytics from "../../utils/analytics";
import styles from "./grammarPage.module.scss";
import { useLocalizedPath } from "../../i18n/localizedPath";
import { CROSS_PAGE_SUGGESTIONS } from "../../consts/crossPageSuggestions";

const levelKeys = ["all", "5", "4", "3", "2", "1"];
const levelI18nKeys = { all: "all", 5: "n5", 4: "n4", 3: "n3", 2: "n2", 1: "n1" };
const statusKeys = ["all", "unlearned", "learned"];

const normalizeSearchText = (value) =>
    String(value || "")
        .normalize("NFKC")
        .toLocaleLowerCase()
        .replace(/[\s~〜・、。,.!?！？…\-–—_'"()（）]/g, "");

// Keep the Japanese-script index separate from its romaji counterpart: NFKD
// would split kana dakuten, but it is useful here for treating ō and o alike.
// The aliases cover the most common Hepburn/Kunrei spellings a learner may
// type, while the unmodified text remains indexed for the existing search.
const normalizeRomajiSearchText = (value) =>
    String(value || "")
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLocaleLowerCase()
        .replace(/[\s~〜・、。,.!?！？…\-–—_'"()（）]/g, "")
        .replace(/([aeiou])\1/g, "$1")
        .replace(/ou/g, "o")
        .replace(/ei/g, "e")
        .replace(/shi/g, "si")
        .replace(/chi/g, "ti")
        .replace(/tsu/g, "tu")
        .replace(/fu/g, "hu")
        .replace(/ji/g, "zi")
        .replace(/sh([aeou])/g, "sy$1")
        .replace(/ch([aeou])/g, "ty$1")
        .replace(/j([aeou])/g, "zy$1");

const particleKana = { は: "わ", へ: "え", を: "お" };

function searchForms(value) {
    const source = String(value || "");
    const particleReading = source.replace(/[はへを]/g, (character) => particleKana[character]);
    return [
        normalizeSearchText(source),
        normalizeRomajiSearchText(kanaToRomaji(source)),
        normalizeRomajiSearchText(kanaToRomaji(particleReading)),
    ].filter(Boolean);
}

function querySearchForms(query) {
    const source = String(query || "");
    const directForm = normalizeSearchText(source);
    // A pure Japanese query should keep its established literal behaviour.
    // Add the transliterated form for romaji-only and mixed-script queries.
    if (/[ぁ-んァ-ヶ一-龯]/.test(source) && !/[a-zA-Z]/.test(source)) return directForm ? [directForm] : [];
    return searchForms(source);
}

function searchableGrammarText(point) {
    const exampleText = point.examples.flatMap((example) => [
        example.japanese,
        example.reading,
        example.english,
        example.translation,
    ]);
    return [
        point.pattern,
        point.title,
        point.meaning,
        point.explanation,
        point.formation,
        ...(point.keywords || []),
        ...exampleText,
    ].filter(Boolean);
}

function GrammarPage() {
    const lp = useLocalizedPath();
    const { t, i18n } = useTranslation("grammarPage");
    const grammarPoints = useGrammarPoints();
    const { status: authStatus } = useAuth();
    const isAuthed = authStatus === "authenticated";
    const location = useLocation();
    const { level: preferredLevel, syncStatus: levelSyncStatus } = useJlptLevel();
    const [level, setLevel] = useState(() => preferredLevel || "all");
    const [status, setStatus] = useState("all");
    const [query, setQuery] = useState("");
    const userChangedLevelRef = useRef(false);
    const { progress, setLearned, syncStatus } = useGrammarProgress();
    const { seen: seenAchievements, markSeen, syncStatus: seenSyncStatus } = useAchievementsSeen();
    const celebratedRef = useRef(new Set());
    const isProgressReady = syncStatus !== "loading";

    const isPt = i18n.language === "pt";
    const currentPath = isPt ? "/pt/grammar" : "/grammar";

    useEffect(() => {
        if (userChangedLevelRef.current || levelSyncStatus === "loading") return;
        if (preferredLevel) setLevel(preferredLevel);
    }, [preferredLevel, levelSyncStatus]);

    function handleLevelChange(key) {
        userChangedLevelRef.current = true;
        setLevel(key);
    }

    function celebrateGrammar(point) {
        const achievement = grammarCelebration(point.id, point.title || point.pattern);
        if (seenSyncStatus === "loading" || seenAchievements[achievement.id] === "seen" || celebratedRef.current.has(achievement.id)) return;
        celebratedRef.current.add(achievement.id);
        markSeen(achievement.id);
        analytics.track("achievement_celebration_displayed", { achievement_type: achievement.kind });
        publishMilestone({ achievement, continueTo: lp("/grammar") });
    }

    const countsByLevel = useMemo(() => {
        const counts = { all: grammarPoints.length };
        levelKeys.slice(1).forEach((key) => {
            counts[key] = grammarPoints.filter((point) => point.jlptLevel === Number(key)).length;
        });
        return counts;
    }, [grammarPoints]);

    const levelFilteredPoints = useMemo(() => {
        if (level === "all") return grammarPoints;
        const numericLevel = Number(level);
        return grammarPoints.filter((point) =>
            point.jlptLevel === numericLevel || point.category === "writing-convention"
        );
    }, [level, grammarPoints]);

    // Build the expanded index only when the localised grammar content changes;
    // each keystroke then only compares short, normalised strings.
    const grammarSearchIndex = useMemo(
        () => new Map(grammarPoints.map((point) => [
            point.id,
            searchableGrammarText(point).flatMap(searchForms),
        ])),
        [grammarPoints]
    );

    const searchFilteredPoints = useMemo(() => {
        const queryForms = querySearchForms(query);
        if (!queryForms.length) return levelFilteredPoints;

        return levelFilteredPoints.filter((point) => {
            const indexedForms = grammarSearchIndex.get(point.id) || [];
            return queryForms.some((queryForm) => indexedForms.some((value) => value.includes(queryForm)));
        });
    }, [grammarSearchIndex, levelFilteredPoints, query]);

    // Independent of the real substring search below: a short, deliberate
    // query (e.g. "te") can point to content that lives on another page
    // entirely, so it's matched as a whole normalized term rather than a
    // substring - otherwise longer queries that merely contain "te" (e.g.
    // "lately") would spam this callout.
    const crossPageSuggestion = useMemo(() => {
        const normalizedQuery = normalizeSearchText(query);
        if (!normalizedQuery) return null;
        return (
            CROSS_PAGE_SUGGESTIONS.find((suggestion) =>
                suggestion.matches.some((term) => normalizeSearchText(term) === normalizedQuery)
            ) || null
        );
    }, [query]);

    const countsByStatus = useMemo(() => {
        const learned = searchFilteredPoints.filter((point) => progress[point.id] === "learned").length;
        return {
            all: searchFilteredPoints.length,
            learned,
            unlearned: searchFilteredPoints.length - learned,
        };
    }, [searchFilteredPoints, progress]);

    const filteredPoints = useMemo(() => {
        if (status === "learned") return searchFilteredPoints.filter((point) => progress[point.id] === "learned");
        if (status === "unlearned") return searchFilteredPoints.filter((point) => progress[point.id] !== "learned");
        return searchFilteredPoints;
    }, [searchFilteredPoints, status, progress]);

    return (
        <div className={styles.page}>
            <Seo
                title={t("seoTitle")}
                description={t("seoDescription")}
                path={currentPath}
                lang={isPt ? "pt-PT" : undefined}
            />
            <Header />
            <main className={styles.main}>
                <section className={styles.container}>
                    <h1 className={styles.title}>{t("title")}</h1>
                    <p className={styles.description}>{t("description")}</p>

                    <div className={styles.card}>
                        <div className={styles.filterSection}>
                            <h2 className={styles.cardTitle}>{t("searchGrammarRules")}</h2>
                            <div className={styles.searchRow}>
                                <label htmlFor="grammar-search" className={styles.visuallyHidden}>
                                    {t("searchGrammarRules")}
                                </label>
                                <input
                                    id="grammar-search"
                                    type="search"
                                    className={styles.searchInput}
                                    value={query}
                                    onChange={(event) => setQuery(event.target.value)}
                                    placeholder={t("searchPlaceholder")}
                                />
                                {query && (
                                    <button type="button" className={styles.clearSearch} onClick={() => setQuery("")}>
                                        {t("clear")}
                                    </button>
                                )}
                            </div>
                        </div>

                        {crossPageSuggestion && (
                            <p className={styles.crossPageSuggestion} role="status" aria-live="polite">
                                {t(`crossPageSuggestions.${crossPageSuggestion.labelKey}.text`)}{" "}
                                <Link to={isPt ? `/pt${crossPageSuggestion.to}` : crossPageSuggestion.to}>
                                    {t(`crossPageSuggestions.${crossPageSuggestion.labelKey}.linkText`)}
                                </Link>
                            </p>
                        )}

                        <div className={styles.filterSection}>
                            <h2 className={styles.cardTitle}>{t("filterByLevel")}</h2>
                            <div className={styles.levelFilterRow}>
                                {levelKeys.map((key) => (
                                    <button
                                        key={key}
                                        type="button"
                                        className={key === level ? styles.levelChipActive : styles.levelChip}
                                        onClick={() => handleLevelChange(key)}
                                        disabled={countsByLevel[key] === 0}
                                    >
                                        {t(`levels.${levelI18nKeys[key]}`)} ({countsByLevel[key]})
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className={styles.filterSection}>
                            <h2 className={styles.cardTitle}>{t("filterByProgress")}</h2>
                            {!isAuthed ? (
                                <p className={styles.emptyText}>
                                    <Link to={lp("/login")} state={{ from: location }}>{t("logInToTrackPrefix")}</Link>{" "}
                                    {t("logInToTrackSuffix")}
                                </p>
                            ) : isProgressReady ? (
                                <div className={styles.levelFilterRow}>
                                    {statusKeys.map((key) => (
                                        <button
                                            key={key}
                                            type="button"
                                            className={key === status ? styles.levelChipActive : styles.levelChip}
                                            onClick={() => setStatus(key)}
                                            disabled={countsByStatus[key] === 0}
                                        >
                                            {t(`statuses.${key}`)} ({countsByStatus[key]})
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <p className={styles.emptyText}>{t("syncingProgress")}</p>
                            )}
                        </div>
                    </div>

                    {filteredPoints.length ? (
                        <ul className={styles.grammarList}>
                            {filteredPoints.map((point) => {
                                const learned = isAuthed && isProgressReady && progress[point.id] === "learned";
                                return (
                                    <li key={point.id} className={`${styles.grammarListItem} ${learned ? styles.learnedCard : ""}`}>
                                        <Link to={isPt ? `/pt/grammar/${point.id}` : `/grammar/${point.id}`} className={styles.grammarCard}>
                                            <div className={styles.grammarCardHeader}>
                                                <span className={styles.grammarPattern}>{point.pattern}</span>
                                                <span className={styles.cardBadges}>
                                                    {learned && <LearnedBadge />}
                                                    {point.category === "writing-convention" ? (
                                                        <span className={styles.jlptBadge}>{t("categories.writingConvention")}</span>
                                                    ) : (
                                                        <span className={styles.jlptBadge}>N{point.jlptLevel}</span>
                                                    )}
                                                </span>
                                            </div>
                                            <p className={styles.grammarMeaning}>{point.meaning}</p>
                                        </Link>
                                        <LearningStatusButton
                                            learned={learned}
                                            onClick={() => {
                                                const marking = !learned;
                                                setLearned(point.id, marking);
                                                if (marking) celebrateGrammar(point);
                                            }}
                                            disabled={!isProgressReady}
                                        />
                                    </li>
                                );
                            })}
                        </ul>
                    ) : (
                        <p className={styles.emptyText}>
                            {query.trim()
                                ? t("noMatches", { query: query.trim() })
                                : t("noPointsForLevel")}
                        </p>
                    )}
                </section>
            </main>
            <Footer />
        </div>
    );
}

export default GrammarPage;
